import { MapsAPILoader } from '@agm/core';
import { Location } from '@angular-material-extensions/google-maps-autocomplete';
import { DOCUMENT } from '@angular/common';
import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
// eslint-disable-next-line import/order
import PlaceResult = google.maps.places.PlaceResult;
import { FieldType } from '@ngx-formly/core';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';

// @dynamic
@Component({
  selector: 'sc-field-google-maps',
  templateUrl: './field-google-maps.component.html',
  styleUrls: ['./field-google-maps.component.scss']
})
export class FieldGoogleMapsComponent extends FieldType implements OnInit, OnDestroy {
  public address$: Observable<string>;
  public zoom: number;
  public latitude: number;
  public longitude: number;
  public selectedAddress: PlaceResult;
  private _geoCoder: any;
  private _geoCoderInitialized: boolean;

  private _address: Subject<string> = new Subject();

  constructor(private _mapsAPILoader: MapsAPILoader, @Inject(DOCUMENT) private _document: Document) {
    super();
  }
  @Input() public set value(value: string) {}

  ngOnInit() {
    this.address$ = this._address.asObservable().pipe(tap(() => window.dispatchEvent(new Event('resize'))));
    this.zoom = 14;

    this._getAddress(this.formControl.value);
  }

  ngOnDestroy() {
    this._destroyPacContainer();
  }

  onLocationSelected(location: Location) {
    this.latitude = location.latitude;
    this.longitude = location.longitude;

    if (!this.to.disabled) {
      const value = `${location.latitude}, ${location.longitude}`;
      if (this.to.config.type === 'coords') {
        this.formControl.setValue(value);
      } else {
        this._getAddress(value);
      }
    }
  }

  markerDragEnd({ coords }) {
    this.latitude = coords.lat;
    this.longitude = coords.lng;
    if (!this.to.disabled) {
      const value = `${coords.lat},${coords.lng}`;
      if (this.to.config.type === 'coords') {
        this.formControl.setValue(value);
      } else {
        this._getAddress(value);
      }
    }
  }

  private _destroyPacContainer() {
    try {
      // have to remove manually
      const pacContainer = this._document.querySelector('.pac-container') as HTMLElement;
      this._document.body.removeChild(pacContainer);
    } catch (error) {}
  }

  private async _initializeGeocoder() {
    return this._mapsAPILoader.load().then(() => {
      this._geoCoderInitialized = true;
      return new google.maps.Geocoder();
    });
  }

  private async _getAddress(value: string) {
    if (!value) {
      console.warn('No coords found!');
      return;
    }
    const [lat, lng] = value.split(',');
    const latitude = Number(lat.trim());
    const longitude = Number(lng.trim());

    if (isNaN(latitude) || isNaN(longitude)) {
      throw new Error('Latitude and longitude is not the correct format! Example: "40.54, 20.56"');
    }

    this.latitude = latitude;
    this.longitude = longitude;

    if (!this._geoCoderInitialized) {
      this._geoCoder = await this._initializeGeocoder();
    }

    this._geoCoder.geocode({ location: { lat: latitude, lng: longitude } }, (results: PlaceResult[], status: string) => {
      if (status === 'OK') {
        if (results[0]) {
          const [place]: PlaceResult[] = results;
          if (this.to.config.type === 'fulladdress') {
            const address = place.address_components;
            const streetNumber = address.find(item => item.types.some(type => type === 'street_number'));
            const streetName = address.find(item => item.types.some(type => type === 'route'));
            const cityName = address.find(item => item.types.some(type => type === 'locality'));

            const country = address.find(item => item.types.some(type => type === 'country'));

            const fullAddress = {
              country: country ? country.short_name : '',
              cityName: cityName ? cityName.short_name : '',
              streetNum: streetNumber ? streetNumber.short_name : '',
              streetName: streetName ? streetName.short_name : '',
              formattedAddress: place.formatted_address ? place.formatted_address : ''
            };
            this.formControl.setValue(fullAddress);
          }

          this._address.next(place.formatted_address);
        }
      }
    });
  }
}
